// Terms of Service.
//
// This is a starting-position legal document. It is NOT a substitute for an
// attorney. If Carol Care grows into a real business with meaningful revenue,
// have a lawyer review and tailor it. For an indie family app at our current
// scale, this is solid baseline coverage.

import { Link } from "react-router-dom";
import { Wordmark } from "../components/Brand";

const LAST_UPDATED = "May 17, 2026";
const ENTITY = "JK Media Group, LLC";
const STATE = "Missouri";

export function Terms() {
  return (
    <div className="min-h-full bg-bg">
      <header className="border-b border-border bg-surface/80 backdrop-blur sticky top-0 z-10">
        <div className="mx-auto max-w-3xl px-5 h-14 flex items-center justify-between">
          <Link to="/" className="block"><Wordmark size="sm" /></Link>
          <Link to="/" className="text-sm text-text-muted hover:text-text">Back to home</Link>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-5 py-12 sm:py-16">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">Terms of Service</h1>
        <p className="mt-2 text-sm text-text-faint">Last updated: {LAST_UPDATED}</p>

        <Section title="The basics">
          <p>
            Carol Care is a free web application that helps families coordinate medical visits for a
            loved one. It's operated by <strong>{ENTITY}</strong> at <a href="https://carolcare.io" className="text-primary hover:underline">carolcare.io</a>.
            By signing in, using, or contributing to Carol Care (collectively, the "Service"), you
            agree to these Terms of Service ("Terms"). If you don't agree, please don't use the Service.
          </p>
          <p className="mt-3">
            These Terms are written in plain language on purpose. We've tried to keep the legalese
            to a minimum while still saying what we need to say.
          </p>
        </Section>

        <Section title="Carol Care is not a medical device">
          <p>
            Carol Care helps families <em>coordinate</em> medical visits. It does not provide medical
            advice, diagnose conditions, recommend treatments, or replace the judgment of a healthcare
            professional. Information shown in the app is whatever you and your family choose to enter —
            the app doesn't verify it.
          </p>
          <p className="mt-3">
            <strong>You are responsible for your family's actual healthcare decisions, attendance, and
            outcomes.</strong> If you're using Carol Care to coordinate something time-sensitive,
            confirm it through your usual channels. Don't rely on the app as the sole record of an
            appointment.
          </p>
        </Section>

        <Section title="Who can use the Service">
          <p>You may use Carol Care if all of these are true:</p>
          <ul className="mt-2 list-disc pl-5 space-y-1.5">
            <li>You are 18 years of age or older (or have a parent/guardian's consent).</li>
            <li>You provide a real email address you control.</li>
            <li>You comply with applicable laws and these Terms.</li>
          </ul>
          <p className="mt-3">
            If you're adding information about another adult, please make sure they're aware and
            comfortable with the family coordinating their care through this app. If you're adding
            information about a minor, you must be their parent or legal guardian.
          </p>
        </Section>

        <Section title="Your account">
          <ul className="mt-2 list-disc pl-5 space-y-1.5">
            <li>Sign-in is by email magic-link or 6-digit code — no passwords. Treat the email account associated with your Carol Care account as the gatekeeper.</li>
            <li>You're responsible for activity that happens under your account.</li>
            <li>If you suspect someone else has accessed your account, email <a href="mailto:support@carolcare.io" className="text-primary hover:underline">support@carolcare.io</a> right away.</li>
            <li>You can delete your account in-app at any time (Notifications → Close account). Deletion is described in the <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.</li>
          </ul>
        </Section>

        <Section title="Acceptable use">
          <p>Don't use Carol Care to:</p>
          <ul className="mt-2 list-disc pl-5 space-y-1.5">
            <li>Impersonate anyone, or sign up using an email that isn't yours.</li>
            <li>Add other people to a care circle without their consent.</li>
            <li>Submit information about a minor without parental or legal-guardian authority.</li>
            <li>Send spam, harassment, or harmful content through the invite or notification system.</li>
            <li>Reverse-engineer, scrape, or attempt to defeat the access controls of the Service.</li>
            <li>Use the Service to violate any law, including healthcare-privacy or anti-spam laws.</li>
            <li>Resell or commercially redistribute the Service.</li>
          </ul>
          <p className="mt-3">
            We can suspend or close any account that violates these rules. We'll usually try to give
            you a heads-up first, but we reserve the right to act immediately for serious violations.
          </p>
        </Section>

        <Section title="What you enter, and what we do with it">
          <p>
            You keep ownership of the information you enter into Carol Care (visit details, provider
            details, notes, member roster, etc.). By using the Service you grant us a non-exclusive,
            royalty-free license to store, display, and transmit that information solely as needed to
            operate the Service for you and the other members of your care circle.
          </p>
          <p className="mt-3">
            We don't sell your data. We don't show ads. The full handling of your data is described in
            the <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>, which is part of these Terms by reference.
          </p>
        </Section>

        <Section title="Donations">
          <p>
            Carol Care is free for every family. Donations are entirely voluntary and don't unlock,
            enhance, or extend any features of the app — they exist only to help cover the cost of
            running the Service.
          </p>
          <ul className="mt-2 list-disc pl-5 space-y-1.5">
            <li>Donations are processed by Venmo or Stripe under their own terms; we don't store your payment-method details.</li>
            <li>Donations are <strong>not tax-deductible</strong>. {ENTITY} is not a registered nonprofit.</li>
            <li>Refunds for donations are at our discretion. If something's gone wrong, email <a href="mailto:support@carolcare.io" className="text-primary hover:underline">support@carolcare.io</a>.</li>
          </ul>
        </Section>

        <Section title="Service availability">
          <p>
            We do our best to keep Carol Care running 24/7, but we don't guarantee uptime, delivery
            speed, or that notifications (email, SMS, push) will arrive in time for any given visit.
            Outages happen. Email providers throttle. Phone carriers drop SMS. Treat the in-app
            calendar as the source of truth and confirm critical appointments through your usual
            channels.
          </p>
          <p className="mt-3">
            We may add, change, or remove features at any time. If we make a significant change that
            affects how you use the Service, we'll try to notify circle admins by email.
          </p>
        </Section>

        <Section title="Disclaimer of warranties">
          <p className="uppercase text-xs font-semibold tracking-wider text-text-muted">
            Required boilerplate, in clear language:
          </p>
          <p className="mt-2">
            Carol Care is provided <strong>"as is"</strong> and <strong>"as available."</strong> To the
            fullest extent permitted by law, we disclaim all warranties — express, implied, or
            statutory — including warranties of merchantability, fitness for a particular purpose,
            non-infringement, and any warranty arising out of course of dealing or usage of trade.
          </p>
          <p className="mt-3">
            We do not warrant that the Service will be uninterrupted, error-free, secure against
            unauthorized access, or that notifications will be delivered on time. <strong>You are not
            entitled to rely on the Service for healthcare decisions.</strong>
          </p>
        </Section>

        <Section title="Limitation of liability">
          <p>
            To the fullest extent permitted by law, <strong>{ENTITY}</strong> and its officers,
            employees, contractors, and agents shall not be liable for:
          </p>
          <ul className="mt-2 list-disc pl-5 space-y-1.5">
            <li>Indirect, incidental, special, consequential, or punitive damages of any kind.</li>
            <li>Lost data, lost profits, lost goodwill, business interruption, or substitute-service costs.</li>
            <li>Missed appointments, healthcare outcomes, or any harm arising from how you, your family members, or your loved one's providers act on (or fail to act on) information shown in the app.</li>
            <li>Acts or omissions of third-party services we rely on (Cloudflare, Resend, Stripe, Twilio, Venmo, Apple, Google, your phone carrier, etc.).</li>
            <li>Events beyond our reasonable control (outages, natural disasters, government actions, network failures).</li>
          </ul>
          <p className="mt-3">
            Our total liability to you for any claim arising out of or relating to the Service is
            <strong> limited to one hundred U.S. dollars ($100 USD)</strong>, or the total amount
            you've donated to us in the twelve months before the claim, whichever is greater. Some
            jurisdictions don't allow this kind of limitation, in which case our liability is limited
            to the smallest amount permitted by applicable law.
          </p>
        </Section>

        <Section title="Indemnification">
          <p>
            You agree to defend, indemnify, and hold harmless {ENTITY} and its officers, employees,
            contractors, and agents from and against any claim, demand, loss, damage, or expense
            (including reasonable attorneys' fees) arising out of:
          </p>
          <ul className="mt-2 list-disc pl-5 space-y-1.5">
            <li>Your use of the Service.</li>
            <li>Your violation of these Terms.</li>
            <li>Your violation of any third-party right, including the privacy, publicity, or intellectual property rights of another person.</li>
            <li>Any content you submit to the Service that causes harm to a third party.</li>
          </ul>
        </Section>

        <Section title="Termination">
          <p>
            You can end your relationship with Carol Care anytime by deleting your account in-app.
            We may suspend or terminate your access — with or without notice — if you violate these
            Terms, if we're required to by law, or if continuing your access exposes us or other users
            to risk.
          </p>
          <p className="mt-3">
            Sections that by their nature should survive termination (including the disclaimers,
            limitation of liability, indemnification, and governing law) will survive.
          </p>
        </Section>

        <Section title="Changes to these Terms">
          <p>
            We may update these Terms over time. We'll update the "Last updated" date above and, for
            material changes, notify circle admins by email. Continued use of the Service after
            changes take effect means you accept the updated Terms.
          </p>
        </Section>

        <Section title="Governing law and disputes">
          <p>
            These Terms are governed by the laws of the State of {STATE}, USA, without regard to its
            conflict-of-laws principles. You and {ENTITY} agree that any legal action or proceeding
            arising out of or related to the Service shall be brought exclusively in the state or
            federal courts located in {STATE}, and you consent to the personal jurisdiction of those
            courts.
          </p>
          <p className="mt-3">
            Before filing any claim, please email <a href="mailto:support@carolcare.io" className="text-primary hover:underline">support@carolcare.io</a> first — most things are
            faster and easier to resolve directly.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions about these Terms? Email <a href="mailto:support@carolcare.io" className="text-primary hover:underline">support@carolcare.io</a>. Replies usually within a day or two.
          </p>
          <p className="mt-3 text-sm text-text-muted">
            Carol Care is operated by {ENTITY}, a {STATE} limited liability company.
          </p>
        </Section>

        <footer className="mt-12 pt-8 border-t border-border text-sm text-text-muted flex items-center justify-between">
          <Wordmark size="sm" />
          <Link to="/" className="hover:text-text">Back to home</Link>
        </footer>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
      <div className="mt-3 text-[15px] leading-relaxed text-text-muted [&_p]:text-text-muted [&_strong]:text-text">{children}</div>
    </section>
  );
}
